<script setup>
import { FwbSidebarItemGroup } from 'flowbite-vue'
import SidebarItem from "@/Components/Common/Helpers/SidebarItem.vue";
import { useHelpers, useTenantTextRules } from "@/helpers";
import ApplicationMark from "@/Components/ApplicationMark.vue";
import { computed } from "vue";

const props = defineProps({
  pageProps: {
    type: Object,
    default: null
  }
});

const currentUser = props.pageProps.auth.user;
const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const navigationVisibility = props.pageProps?.app?.navigationVisibility || {};

const isMenuVisible = (key) => navigationVisibility?.[key] !== false;
const tenantManagerOnlyMode = computed(() => props.pageProps.app?.tenantManagerOnlyMode === true);
const sidebarProjectManagementLabel = computed(() =>
  resolveTextRule('sidebar.project_management', 'Project Management', 'sidebar.project_management')
);
const sidebarPurchaseOrdersLabel = computed(() =>
  resolveTextRule('sidebar.purchase_orders', 'Purchase Orders', 'sidebar.purchase_orders')
);
</script>

<template>
  <aside class="top-0 left-0 transition-transform w-full h-full relative">
    <div class="h-full px-3 py-4 overflow-y-auto bg-gray-800">
      <div class="space-y-2 font-medium">
        <div class="mb-3 flex justify-center">
          <ApplicationMark class="h-14" />
        </div>
        <template v-if="tenantManagerOnlyMode">
          <fwb-sidebar-item-group>
            <SidebarItem
              icon="server"
              label="Tenant Manager"
              :href="route('admin.tenant-manager.index')"
              :exact="false"
            />
          </fwb-sidebar-item-group>
        </template>
        <template v-else>
        <fwb-sidebar-item-group>
          <SidebarItem v-if="isMenuVisible('dashboard')" svg="GridOutline" label="Dashboard" :href="route('dashboard')" />
          <SidebarItem svg="BriefcaseOutline" :label="sidebarProjectManagementLabel" :href="route('admin.projects.index')"
            v-if="isMenuVisible('project_management') && helpers.userHasPermission(currentUser, 'project_management')" />
          <SidebarItem svg="CartOutline" :label="sidebarPurchaseOrdersLabel" :href="route('admin.purchase-orders.index')"
            v-if="isMenuVisible('purchase_orders') && helpers.userHasPermission(currentUser, 'purchase_order')" />
          <SidebarItem svg="FolderPlusOutline" label="New Workorder" :href="route('admin.workorders.create')"
            v-if="isMenuVisible('new_workorder') && helpers.userHasPermission(currentUser, 'work_order')" />
          <SidebarItem svg="DatabaseOutline" label="Sample Database" :href="route('admin.samples.index')"
            v-if="isMenuVisible('sample_database') && helpers.userHasPermission(currentUser, 'sample_database')" />
          <!--<SidebarItem svg="DrawSquareOutline" label="Patterns" href="/content/inner.php"
            v-if="helpers.userHasPermission(currentUser, 'patterns')" />-->
          <SidebarItem icon="file-invoice" label="Accounting" :href="route('accounting.index')"
            v-if="isMenuVisible('accounting') && helpers.userHasPermission(currentUser, 'accounting')" />
          <SidebarItem icon="level-up-alt" label="Production" :href="route('production.index')"
            v-if="isMenuVisible('production') && helpers.userHasPermission(currentUser, 'production')" />
          <SidebarItem svg="CalendarMonthOutline" label="Calendar" :href="route('internal.office-calendar.index')"
            v-if="isMenuVisible('calendar') && helpers.userHasPermission(currentUser, 'calendar')" />
          <SidebarItem icon="table" label="Sales" :href="route('sales.index')" :exact="true"
            v-if="isMenuVisible('sales') && helpers.userHasPermission(currentUser, 'sales')" />
          <SidebarItem icon="book" label="Lookbook" :href="route('sales.lookbook.index')"
            v-if="isMenuVisible('lookbook') && (helpers.userHasPermission(currentUser, 'lookbook_read_only') || helpers.userHasPermission(currentUser, 'can_view_lookbook') || helpers.userHasPermission(currentUser, 'admin'))" />
          <SidebarItem icon="chart-line" label="Sales Plan" :href="route('sales.salesplan.index')"
            v-if="isMenuVisible('sales_plan') && helpers.userHasPermission(currentUser, 'can_view_sales_plan')"/>
          <SidebarItem icon="user-cog" label="Human Resources" :href="route('human-resources.index')"
            v-if="isMenuVisible('human_resources') && helpers.userHasPermission(currentUser, 'humanresources')" />
          <SidebarItem icon="cogs" label="Operations" :href="route('operations.index')"
            v-if="isMenuVisible('operations') && helpers.userHasPermission(currentUser, 'operations')" />
          <SidebarItem v-if="isMenuVisible('report_bugs')" svg="FolderPlusOutline" label="Report Bugs" :href="route('internal.report-issues.index')" />
          <SidebarItem icon="tshirt" label="Style Generator" :href="route('inventory.code-generator')"
            v-if="isMenuVisible('style_generator') && helpers.userHasPermission(currentUser, 'style_generator') "></SidebarItem>

        </fwb-sidebar-item-group>
        <fwb-sidebar-item-group>
          <hr class="text-gray-400">
        </fwb-sidebar-item-group>
        <fwb-sidebar-item-group>
          <SidebarItem svg="UserCircleOutline" label="Administration" :href="route('admin.dashboard')"
            v-if="isMenuVisible('administration') && helpers.userHasPermission(currentUser, 'admin')" />
          <SidebarItem label="Users" icon="users" :href="route('admin.users.active')"
            v-if="isMenuVisible('users') && helpers.userHasPermission(currentUser, 'admin')" />
          <SidebarItem svg="StoreOutline" label="Vendors" :href="route('admin.vendors.active')"
            v-if="isMenuVisible('vendors') && helpers.userHasPermission(currentUser, 'vendor_database')" />
          <SidebarItem svg="Building" label="Companies" :href="route('admin.companies.active')"
            v-if="isMenuVisible('companies') && helpers.userHasPermission(currentUser, 'client_database')" />
          <SidebarItem svg="FileSearch" label="Knowledge-base" :href="route('admin.knowledgebase.index')"
            v-if="isMenuVisible('knowledgebase') && helpers.userHasPermission(currentUser, 'admin')" />
          <SidebarItem v-if="isMenuVisible('measurements')" label="Measurements" icon="ruler" :href="route('admin.measurements.index')" />
          <SidebarItem label="Tracking Log" icon="clipboard-check" :href="route('admin.audit-log.index')"
            v-if="isMenuVisible('tracking_log') && helpers.userHasPermission(currentUser, 'tracking')" />
          <!--<SidebarItem label="Tracking Log" icon="clipboard-check" href="/content/admin/track_log/track_log.php"
                       v-if="helpers.userHasPermission(currentUser, 'tracking')"/>-->
          <SidebarItem svg="CalendarMonthOutline" label="Calendar Report" :href="route('admin.office-calendar.index')"
            v-if="isMenuVisible('calendar_report') && helpers.userHasPermission(currentUser, 'admin')" />
          <SidebarItem label="Tax" icon="id-card" :href="route('admin.tax.index')"
            v-if="isMenuVisible('tax') && helpers.userHasPermission(currentUser, 'admin')" />
          <SidebarItem label="Carriers" icon="shipping-fast" :href="route('admin.carrier.index')"
            v-if="isMenuVisible('carriers') && helpers.userHasPermission(currentUser, 'admin')" />
          <!--<SidebarDropdownItem label="Packages" icon="box" v-if="helpers.userHasPermission(currentUser, 'admin')">
            <SidebarItem label="Image File" href="/content/admin/image_file/image_file_list.php" />
            <SidebarItem label="Element" href="/content/admin/element/element_list.php" />
          </SidebarDropdownItem>-->
          <SidebarItem svg="FolderPlusOutline" label="Workorder" :href="route('admin.workorders.index')"
            v-if="isMenuVisible('workorder') && helpers.userHasPermission(currentUser, 'work_order')" />
          <SidebarItem label="Type of Global Pricing" icon="tags" :href="route('admin.pricing.index')"
            v-if="isMenuVisible('global_pricing') && helpers.userHasPermission(currentUser, 'admin')" />
          <!--<SidebarItem label="Conveyor" icon="comments-dollar" href="/content/admin/sample_database/conveyor_edit01.php"
                       v-if="helpers.userHasPermission(currentUser, 'admin')"/>-->
          <SidebarItem svg="FileExportOutline" label="Incoming Fill Orders" :href="route('admin.fill-orders.incoming')"
            v-if="isMenuVisible('incoming_fill_orders') && helpers.userHasPermission(currentUser, 'incoming_fill_order')" />
          <SidebarItem svg="ClipboardListOutline" label="Inventory" :href="route('inventory.index')"
            v-if="isMenuVisible('inventory') && helpers.userHasPermission(currentUser, 'inventory')" />
          <SidebarItem svg="MessagesOutline" label="Quotes" :href="route('admin.quotes.index')"
            v-if="isMenuVisible('quotes') && helpers.userHasPermission(currentUser, 'generate_request')" />
          <SidebarItem icon="envelope" label="Email Templates" :href="route('admin.email-templates.index')"
            v-if="isMenuVisible('email_templates') && helpers.userHasPermission(currentUser, 'admin')" />
        </fwb-sidebar-item-group>
        </template>
      </div>
    </div>
  </aside>
</template>
